/**
 * The assistant's answers, rendered safely: the Markdown it writes (headings,
 * lists, tables, bold, italics, code, links) becomes React elements. No HTML
 * is ever injected, and only https links are made clickable.
 */

import type { ReactNode } from 'react';

const INLINE = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

function inline(text: string, key: string): ReactNode[] {
  return text.split(INLINE).map((part, index) => {
    const id = `${key}-${index}`;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4)
      return <strong key={id}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2)
      return <code key={id}>{part.slice(1, -1)}</code>;
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2)
      return <em key={id}>{part.slice(1, -1)}</em>;
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
    if (link) {
      const [, label = '', href = ''] = link;
      return href.startsWith('https://') ? (
        <a key={id} href={href} target="_blank" rel="noreferrer noopener">
          {label}
        </a>
      ) : (
        label
      );
    }
    return part;
  });
}

function table(lines: string[], key: string): ReactNode {
  const cells = (line: string) =>
    line
      .replace(/^\||\|$/g, '')
      .split('|')
      .map((cell) => cell.trim());
  const [head = '', , ...body] = lines;
  return (
    <div className="as-table" key={key}>
      <table>
        <thead>
          <tr>
            {cells(head).map((cell, index) => (
              <th key={index}>{inline(cell, `${key}-h${index}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((line, row) => (
            <tr key={row}>
              {cells(line).map((cell, index) => (
                <td key={index}>{inline(cell, `${key}-${row}-${index}`)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type Block = { kind: 'p' | 'ul' | 'ol' | 'table' | 'h'; lines: string[]; level?: number };

/** Adds a line to the open block of this kind, or opens a new one. */
function append(out: Block[], kind: Block['kind'], line: string): void {
  const last = out.at(-1);
  if (last?.kind === kind) last.lines.push(line);
  else out.push({ kind, lines: [line] });
}

function blocks(text: string): Block[] {
  const out: Block[] = [];
  for (const raw of text.replace(/\r/g, '').split('\n')) {
    const line = raw.trimEnd();
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (!line.trim()) out.push({ kind: 'p', lines: [] });
    else if (heading)
      out.push({ kind: 'h', lines: [heading[2] ?? ''], level: (heading[1] ?? '#').length });
    else if (/^\s*[-*]\s+/.test(line)) append(out, 'ul', line.replace(/^\s*[-*]\s+/, ''));
    else if (/^\s*\d+[.)]\s+/.test(line)) append(out, 'ol', line.replace(/^\s*\d+[.)]\s+/, ''));
    else if (line.trim().startsWith('|')) append(out, 'table', line.trim());
    else append(out, 'p', line);
  }
  return out.filter((block) => block.lines.length > 0);
}

export function Markdown({ text }: { text: string }): ReactNode {
  return (
    <div className="as-md">
      {blocks(text).map((block, index) => {
        const key = `b${index}`;
        if (block.kind === 'h') return <h4 key={key}>{inline(block.lines[0] ?? '', key)}</h4>;
        if (block.kind === 'ul')
          return (
            <ul key={key}>
              {block.lines.map((line, item) => (
                <li key={item}>{inline(line, `${key}-${item}`)}</li>
              ))}
            </ul>
          );
        if (block.kind === 'ol')
          return (
            <ol key={key}>
              {block.lines.map((line, item) => (
                <li key={item}>{inline(line, `${key}-${item}`)}</li>
              ))}
            </ol>
          );
        if (block.kind === 'table' && block.lines.length >= 2) return table(block.lines, key);
        return <p key={key}>{inline(block.lines.join(' '), key)}</p>;
      })}
    </div>
  );
}
